'use client';

import React, { useCallback } from 'react';
import { useWorkspaceRouter } from '@/app/features/workspace/routing/useWorkspaceRouter';
import { useSelector } from 'react-redux';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useTrackAppAccess } from '@/app/hooks/useTrackAppAccess';
import { useUserAppAccessWiring } from '@/app/hooks/useUserAppAccessWiring';
import { toShellRoute } from '@/app/features/workspace/utils/shellRoute';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import type { UserAppAccessSummaryDto } from '@/app/services/api/userAppAccessService';
import {
  selectFavorites,
  selectFavoritesIsLoading,
  selectRecents,
  selectRecentsIsLoading,
} from '@/app/store/slices/userAppAccessSlice';
import FavoritesSection from './components/FavoritesSection';
import RecentsSection from './components/RecentsSection';
import type { WorkspaceViewProps } from './types';

/**
 * Default landing content for the standard tenant workspace — desktop only.
 * Viewport gating and the mobile tile-grid rendering live in `app/workspace/page.tsx`
 * (mainstream big-tech pattern: per-tenant customization on desktop, uniform mobile).
 */
const DefaultWorkspaceView = React.memo(function DefaultWorkspaceView(props: WorkspaceViewProps) {
  const { tenant } = props;
  const { t } = useTranslation('workspace');
  const router = useWorkspaceRouter();
  const trackAppAccess = useTrackAppAccess();

  const favorites = useSelector(selectFavorites);
  const favoritesLoading = useSelector(selectFavoritesIsLoading);
  const recents = useSelector(selectRecents);
  const recentsLoading = useSelector(selectRecentsIsLoading);
  // Single tenant-logo source (spec: tenant-branding-identity).
  const tenantLogoUrl: string | null = useSelector((s: any) => s.workspace.tenantLogoUrl);

  useUserAppAccessWiring({
    scope: 'workspace',
    tenantId: tenant?.tenantId ? Number(tenant.tenantId) : null,
    isAuthenticated: true,
  });

  const onTileClick = useCallback(
    (app: UserAppAccessSummaryDto) => {
      trackAppAccess(app.id);
      router.push(toShellRoute(app, workspaceHref(tenant)));
    },
    [router, trackAppAccess, tenant]
  );

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        {tenantLogoUrl && (
          <img
            src={tenantLogoUrl}
            alt={tenant.tenantName}
            className="h-8 w-auto object-contain rounded"
          />
        )}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-primary">
            {tenant.tenantName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t('subtitle')}</p>
        </div>
      </div>

      <FavoritesSection
        favorites={favorites}
        isLoading={favoritesLoading}
        onTileClick={onTileClick}
      />

      <RecentsSection
        recents={recents}
        isLoading={recentsLoading}
        onTileClick={onTileClick}
      />

      <div className="rounded-xl border border-border/60 bg-card p-6">
        <h2 className="text-lg font-semibold mb-1">{t('title')}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('tlt.welcome.apps.description')}
        </p>
      </div>
    </div>
  );
});

DefaultWorkspaceView.displayName = 'DefaultWorkspaceView';
export default DefaultWorkspaceView;
